import { Label, Select, type SelectItem } from "@fronterahealth/frontera-ui-components";

import { EhrIntegrationTypeEnum } from "@api/rest/types-and-hooks";
import { InfoTooltip } from "@components/InfoTooltip";
import {
  EHR_INTEGRATION_TYPE_LABELS,
  EHR_INTEGRATION_TYPE_TOOLTIP_SENTENCES,
} from "@pages/Organizations/ehrIntegrationType";

const READ_ONLY_TOOLTIP = "Shared org login isn't available for this EHR yet.";

// Built from only the types actually offered — mentioning a type that
// isn't an allowed option for this EHR (e.g. "CentralReach data warehouse" on an EHR
// that doesn't support it) would be misleading.
const editableTooltipText = (allowed: readonly EhrIntegrationTypeEnum[]): string =>
  allowed
    .map((type) =>
      EHR_INTEGRATION_TYPE_TOOLTIP_SENTENCES[type]
        ? `${EHR_INTEGRATION_TYPE_LABELS[type] ?? type}: ${EHR_INTEGRATION_TYPE_TOOLTIP_SENTENCES[type]}`
        : (EHR_INTEGRATION_TYPE_LABELS[type] ?? type),
    )
    .join(" ");

const toItem = (type: EhrIntegrationTypeEnum): SelectItem => ({
  id: type,
  primary: EHR_INTEGRATION_TYPE_LABELS[type] ?? type,
});

interface EhrIntegrationTypeFieldProps {
  // The selection shown, which may be a pending (unsaved) choice.
  value: EhrIntegrationTypeEnum;
  // What the org is saved as. Options are built from this, not `value`, so
  // a saved type outside the allowlist stays selectable after another one
  // is picked.
  savedValue: EhrIntegrationTypeEnum;
  allowed: readonly EhrIntegrationTypeEnum[];
  onChange: (value: EhrIntegrationTypeEnum) => void;
  disabled?: boolean;
  // Locks the field with this tooltip regardless of the allowed options
  // (e.g. the viewer lacks permission to change it).
  readOnlyReason?: string;
}

export const EhrIntegrationTypeField: React.FC<EhrIntegrationTypeFieldProps> = ({
  value,
  savedValue,
  allowed,
  onChange,
  disabled,
  readOnlyReason,
}) => {
  // Keep the saved value selectable even if the backend's allowlist omits it,
  // so the field shows it as a real option instead of forcing a switch.
  const options = allowed.includes(savedValue) ? allowed : [savedValue, ...allowed];
  const items = options.map(toItem);
  // The same object that's in `items`, as other Select usages pass.
  const selected = items.find((item) => item.id === value) ?? toItem(value);
  // The backend sends the current value as the only option for EHRs without a
  // switchable mode, so one option means read-only.
  const readOnly = !!readOnlyReason || options.length <= 1;
  const locked = readOnly || !!disabled;

  return (
    <div className="w-64">
      {/* Visible label row so the tooltip sits beside the label, like the
          checkboxes above. Select's own label is kept for screen readers
          (it's what names the combobox) but visually hidden; its built-in
          `tooltipText` can't be used — it's the Material Tailwind Tooltip
          that never opens in basecamp (see InfoTooltip). */}
      <div className="flex items-center gap-2 leading-6">
        <span aria-hidden="true">
          <Label displayType="normal" size="small" colorType="secondary">
            EHR sign-in method
          </Label>
        </span>
        <InfoTooltip
          text={readOnlyReason ?? (readOnly ? READ_ONLY_TOOLTIP : editableTooltipText(options))}
          label="About EHR sign-in method"
          iconClassName="h-3.5 w-3.5 text-text-tertiary"
        />
      </div>
      {/* Select's `disabled` only hides the chevron — it isn't forwarded to
          the Combobox, so the dropdown still opens. `inert` blocks mouse and
          keyboard interaction for real when the field is locked, but also
          drops it from the accessibility tree, so the value is announced
          from outside the inert wrapper instead. */}
      {locked && <span className="sr-only">{`EHR sign-in method: ${selected.primary}`}</span>}
      <div className="[&_[data-testid=select-label]]:sr-only" inert={locked}>
        <Select
          title="EHR sign-in method"
          placeholderText="Select a type"
          items={items}
          selected={selected}
          setSelected={(item) => onChange(item.id as EhrIntegrationTypeEnum)}
          disabled={locked}
        />
      </div>
    </div>
  );
};
